import { environment } from 'src/environments/environment';
import { Settings } from '../../constants/settings.constant';
import { Currency } from '../../enums/i18n/currency.enum';
import { SchemaTypes } from '../../enums/schema-types.enum';
import { Breadcrumb } from '../../models/dto/breadcrumb';
import {
  Product as ProductDto,
  ProductVariant,
} from '../../models/dto/catalog/product';
import { Taxon } from '../../models/dto/catalog/taxon';
import { Variant } from '../../models/dto/catalog/variant';
import { Channel, Push, PushCode } from '../../models/dto/channel';
import { FaqBlock, TaxonSeoBlock } from '../../models/dto/content/block';
import { FaqCategories, FaqQuestion } from '../../models/dto/content/faq';
import { NewsDetail } from '../../models/dto/content/news-detail';
import { Image } from '../../models/dto/image';
import { Pagination } from '../../models/dto/pagination';
import {
  BreadcrumbList,
  FAQPage,
  ItemList,
  NewsArticle,
  Organization,
  Product,
  QuestionEntity,
} from '../../models/sys/schema-org';
import { Network } from '../../models/ui/network';
import { networksMapper } from './ui-mappers.utils';

const MAVIC_NAME: string = 'Mavic';
const MAVIC_LOGO_URL: string = environment.baseUrl + '/images/logo.svg';

export const organizationMapper = (
  route: string,
  channel?: Channel
): Organization => {
  return {
    '@type': SchemaTypes.organization,
    name: MAVIC_NAME,
    url: environment.baseUrl + route,
    logo: MAVIC_LOGO_URL,
    description:
      channel?.blocs?.find((value: Push) => value.code === PushCode.description)
        ?.text ?? '',
    sameAs: channel
      ? networksMapper(channel).map((network: Network) => network.url)
      : [],
  };
};

export const newsArticleMapper = (news?: NewsDetail): NewsArticle => {
  return {
    '@type': SchemaTypes.newsArticle,
    headline: news?.h1 ?? news?.title ?? '',
    image:
      news?.images
        ?.map((image: Image) => image.cachedPath ?? image.path)
        .filter((url?: string): url is string => !!url) ?? [],
    datePublished: toISOString(news?.createdAt),
    dateModified: toISOString(news?.updatedAt),
    author: {
      '@type': SchemaTypes.organization,
      name: MAVIC_NAME,
      url: environment.baseUrl,
    },
    publisher: {
      '@type': SchemaTypes.organization,
      name: MAVIC_NAME,
      logo: {
        '@type': SchemaTypes.imageObject,
        url: MAVIC_LOGO_URL,
      },
    },
  };
};

const toISOString = (date?: Date | string): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed: Date = new Date(date);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

export const breadcrumbsMapper = (
  breadcrumbs?: Breadcrumb[]
): BreadcrumbList => {
  const all: Breadcrumb[] = breadcrumbs ?? [];

  const items: Breadcrumb[] = all.filter(
    (breadcrumb: Breadcrumb, index: number) =>
      !!breadcrumb.link || index === all.length - 1
  );

  return {
    '@type': SchemaTypes.breadcrumbList,
    itemListElement: items.map((breadcrumb: Breadcrumb, index: number) =>
      breadcrumb.link
        ? {
            '@type': SchemaTypes.listItem,
            name: breadcrumb.title ?? '',
            position: (index + 1).toString(10),
            item: environment.baseUrl + breadcrumb.link,
          }
        : {
            '@type': SchemaTypes.listItem,
            name: breadcrumb.title ?? '',
            position: (index + 1).toString(10),
          }
    ),
  };
};

export const taxonMapper = (
  prepareRoute: (slug?: string) => string,
  taxon?: Taxon,
  variants?: Pagination<Variant>
): ItemList => {
  return {
    '@type': SchemaTypes.itemList,
    name: taxon?.h1 ?? '',
    numberOfItems: variants?.meta?.totalItems?.toString(10) ?? '0',
    itemListElement:
      variants?.data.map((variant: Variant, index: number) => ({
        '@type': SchemaTypes.listItem,
        name: variant.title ?? '',
        position: (index + 1).toString(10),
        item: prepareRoute(variant?.slug),
      })) ?? [],
  };
};

export const productMapper = (
  product?: ProductDto,
  variant?: ProductVariant
): Product => {
  return {
    '@type': SchemaTypes.product,
    brand: 'Mavic',
    name: variant?.name ?? '',
    description: product?.shortDescription ?? '',
    image: variant?.images?.[0]?.cachedPath ?? '',
    sku: variant?.code ?? '',
    aggregateRating:
      product?.reviews?.average && product?.reviews?.count
        ? {
            '@type': SchemaTypes.aggregateRating,
            ratingValue: product.reviews.average.toString(10),
            ratingCount: product.reviews.count.toString(10),
            bestRating: Settings.maxRating.toString(10),
            worstRating: '1',
          }
        : undefined,
    offers: {
      '@type': SchemaTypes.offer,
      price: variant?.price?.price
        ? (variant.price.price / 100).toFixed(2)
        : '0',
      priceCurrency: variant?.price?.currency ?? Currency.eur,
      availability: variant?.stock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    },
  };
};

const processTaxonSeoBlock = (
  taxonSeoBlock?: TaxonSeoBlock[],
  cleaner?: (text: string) => string
): QuestionEntity[] => {
  if (!taxonSeoBlock) {
    return [];
  }

  const entities: QuestionEntity[] = [];
  taxonSeoBlock.forEach((block: TaxonSeoBlock) => {
    if (block.texts) {
      const taxonEntities: QuestionEntity[] = block.texts
        .map((text: string) => {
          const textContent: string = cleaner ? cleaner(text) : text;

          // Find question (text ending with ?)
          const questionMatch: RegExpExecArray | null = /^([^?]+\?)/.exec(
            textContent
          );
          if (!questionMatch) {
            return null;
          }

          const question: string = questionMatch[1].trim();
          const answer: string = textContent
            .substring(questionMatch[0].length)
            .trim();

          return {
            '@type': SchemaTypes.question as const,
            name: question,
            acceptedAnswer: {
              '@type': SchemaTypes.answer as const,
              text: answer,
            },
          };
        })
        .filter(
          (item: QuestionEntity | null): item is QuestionEntity => item !== null
        );
      entities.push(...taxonEntities);
    }
  });

  return entities;
};

const processFaqBlocks = (
  faqBlock?: FaqBlock[],
  cleaner?: (text: string) => string
): QuestionEntity[] => {
  if (!faqBlock) {
    return [];
  }

  const entities: QuestionEntity[] = [];
  faqBlock.forEach((block: FaqBlock) => {
    if (block.categories) {
      const faqEntities: QuestionEntity[] = block.categories
        .flatMap((category: FaqCategories) => category.questions || [])
        .filter((item: FaqQuestion) => item.title && item.text)
        .map((item: FaqQuestion) => {
          const question: string =
            cleaner && item.title ? cleaner(item.title) : (item.title ?? '');
          const answer: string =
            cleaner && item.text ? cleaner(item.text) : (item.text ?? '');
          return {
            '@type': SchemaTypes.question as const,
            name: question,
            acceptedAnswer: {
              '@type': SchemaTypes.answer as const,
              text: answer,
            },
          };
        });
      entities.push(...faqEntities);
    }
  });

  return entities;
};

export const faqPageMapper = (
  taxonSeoBlock?: TaxonSeoBlock[],
  faqBlock?: FaqBlock[],
  cleaner?: (text: string) => string
): FAQPage => {
  const entities: QuestionEntity[] = [
    ...processTaxonSeoBlock(taxonSeoBlock, cleaner),
    ...processFaqBlocks(faqBlock, cleaner),
  ];

  return {
    '@type': SchemaTypes.faqPage,
    mainEntity: entities,
  };
};
